import { red } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';

import { ParagonColors } from '@paragon/client-tools';

const error = red;

declare module '@mui/material/styles' {
  interface Palette {
    selectedList?: {
      main: string;
    };
    canvas: {
      main: string;
    };
    backContrast: {
      main: string;
      light: string;
      dark: string;
    };
    logoText: {
      main: string;
    };
    paragon: {
      main: string;
    };
    border: {
      main: string;
    };
    appBar: {
      main: string;
      text: string;
    };
  }

  interface PaletteOptions {
    selectedList?: {
      main: string;
    };
    canvas?: {
      main: string;
    };
    backContrast?: {
      main: string;
      light: string;
      dark: string;
    };
    logoText?: {
      main: string;
    };
    paragon?: {
      main: string;
    };
    border?: {
      main: string;
    };
    appBar: {
      main: string;
      progress: string;
      text: string;
      subtleText: string;
    };
  }
}

export const darkTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    // MUI currently applies a semi-transparent gradient over dialogs causing the specified theme colors to be darker or lighter than intended.
    // This code removes that gradient, which they apply as a backgroundImage, so the intended theme colors are properly displayed.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          borderColor: '#ffffff',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: ParagonColors.green,
          },
        },
      },
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: error[500],
    },
    appBar: {
      main: ParagonColors.background,
      text: '#fff',
      progress: ParagonColors.paragold,
      subtleText: '#c0c0c0',
    },
    background: {
      default: ParagonColors.background,
      paper: ParagonColors.background,
    },
    backContrast: {
      main: '#2d2d2d',
      light: '#3A3A3A',
      dark: '#222222',
    },
    canvas: {
      main: ParagonColors.canvasDark,
    },
    logoText: {
      main: '#fff',
    },
  },
});

export const lightTheme = createTheme({
  typography: {
    fontFamily: 'Roboto Condensed, Figtree, sans-serif',
    // Figtree Semi-Bold for the App Header/Pargon name
    h1: {
      fontSize: '1.2rem',
      fontFamily: 'Figtree',
      fontWeight: 600,
      webkitFontSmoothing: 'antialiased',
    },
    // Figtree Regular for Primary Headings
    h2: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h3: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h4: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h5: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    h6: {
      fontFamily: 'Figtree',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Regular for Standard Headings and Actions
    subtitle1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    subtitle2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    button: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    overline: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body1: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    body2: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 400,
      webkitFontSmoothing: 'antialiased',
    },
    // Roboto Condensed Light for captions
    caption: {
      fontFamily: 'Roboto Condensed',
      fontStyle: 'normal',
      fontWeight: 300,
      webkitFontSmoothing: 'antialiased',
    },
  },
  components: {
    // MUI currently applies a semi-transparent gradient over dialogs causing the specified theme colors to be darker or lighter than intended.
    // This code removes that gradient, which they apply as a backgroundImage, so the intended theme colors are properly displayed.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
  },
  palette: {
    primary: {
      main: ParagonColors.green,
    },
    secondary: {
      main: ParagonColors.paragold,
    },
    error: {
      main: error[500],
    },
    appBar: {
      main: '#505050',
      progress: ParagonColors.paragold,
      text: '#fff',
      subtleText: '#c0c0c0',
    },
    background: {
      default: '#EEEEEE',
      paper: '#EEEEEE',
    },
    backContrast: {
      main: '#eee',
      light: '#fff',
      dark: '#ccc',
    },
    canvas: {
      main: ParagonColors.canvasLight,
    },
    logoText: {
      main: '#303030',
    },
  },
});
